import type { MatchCriteria, Rule, RuleRunResult, Scope, TextCase, Transform } from "./types";

type Reporter = (message: string) => void;

interface RangeRef {
  label: string;
  range: Excel.Range;
}

interface CellTransformResult {
  props?: Excel.SettableCellProperties;
  value?: unknown;
}

export async function applyRules(
  rules: Rule[],
  scope: Scope,
  report: Reporter = () => {}
): Promise<RuleRunResult[]> {
  const enabled = rules.filter((r) => r.enabled);
  if (enabled.length === 0) {
    report("no enabled rules");
    return [];
  }

  const regexes = compileRegexes(enabled);

  const stats: Record<string, { matched: number; changed: number }> = {};
  for (const rule of enabled) {
    stats[rule.id] = { matched: 0, changed: 0 };
  }

  await Excel.run(async (context) => {
    const ranges = await collectRanges(context, scope);
    if (ranges.length === 0) {
      report("no cells in scope");
      return;
    }

    for (const { label, range } of ranges) {
      range.load(["rowCount", "columnCount", "values", "valueTypes"]);
      const propsResult = range.getCellProperties({
        format: {
          fill: { color: true },
          font: { color: true, bold: true, italic: true },
        },
      });
      // eslint-disable-next-line office-addins/no-context-sync-in-loop -- one sync per scope range; intentional, ranges may live on different sheets
      await context.sync();

      const rows = range.rowCount;
      const cols = range.columnCount;
      if (rows === 0 || cols === 0) continue;

      report(`evaluating ${label}: ${(rows * cols).toLocaleString()} cells`);

      // ClientResult.value is populated by context.sync automatically; the lint rule
      // mis-flags it as a Range navigation property.
      // eslint-disable-next-line office-addins/load-object-before-read
      const cellPropsGrid = propsResult.value;
      const valuesGrid = range.values;
      const valueTypesGrid = range.valueTypes;

      const propUpdates: Excel.SettableCellProperties[][] = [];
      const valueUpdates: unknown[][] = [];
      let valueUpdatesNeeded = false;

      for (let r = 0; r < rows; r++) {
        const propRow: Excel.SettableCellProperties[] = [];
        const valueRow: unknown[] = [];
        for (let c = 0; c < cols; c++) {
          const cellProps = cellPropsGrid[r]?.[c] ?? {};
          const cellValue = valuesGrid[r]?.[c];
          const cellValueType = valueTypesGrid[r]?.[c];

          let propUpdate: Excel.SettableCellProperties = {};
          let valueUpdate: unknown = cellValue;
          const rulesThatChanged: string[] = [];

          for (const rule of enabled) {
            if (matches(rule.match, cellProps, cellValue, regexes.get(rule.id))) {
              stats[rule.id].matched++;
              const result = transform(rule.apply, cellProps, cellValue, cellValueType);
              const wroteSomething = result.props !== undefined || result.value !== undefined;
              if (wroteSomething) {
                rulesThatChanged.push(rule.id);
                if (result.props) {
                  propUpdate = mergeProps(propUpdate, result.props);
                }
                if (result.value !== undefined) {
                  valueUpdate = result.value;
                  valueUpdatesNeeded = true;
                }
              }
            }
          }

          for (const ruleId of rulesThatChanged) {
            stats[ruleId].changed++;
          }

          propRow.push(propUpdate);
          valueRow.push(valueUpdate);
        }
        propUpdates.push(propRow);
        valueUpdates.push(valueRow);
      }

      range.setCellProperties(propUpdates);
      if (valueUpdatesNeeded) {
        range.values = valueUpdates as (string | number | boolean)[][];
      }
    }

    await context.sync();
  });

  return enabled.map((rule) => ({
    ruleId: rule.id,
    ruleName: rule.name,
    cellsMatched: stats[rule.id].matched,
    cellsChanged: stats[rule.id].changed,
  }));
}

function compileRegexes(rules: Rule[]): Map<string, RegExp> {
  const out = new Map<string, RegExp>();
  for (const rule of rules) {
    if (rule.match.textRegex) {
      try {
        out.set(rule.id, new RegExp(rule.match.textRegex.pattern, rule.match.textRegex.flags));
      } catch (err) {
        const reason = err instanceof Error ? err.message : String(err);
        throw new Error(`Rule "${rule.name}": invalid regex (${reason})`);
      }
    }
  }
  return out;
}

async function collectRanges(context: Excel.RequestContext, scope: Scope): Promise<RangeRef[]> {
  if (scope === "selection") {
    const r = context.workbook.getSelectedRange();
    r.load("address");
    await context.sync();
    return [{ label: r.address, range: r }];
  }
  if (scope === "sheet") {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    sheet.load("name");
    const r = sheet.getUsedRangeOrNullObject(true);
    await context.sync();
    return r.isNullObject ? [] : [{ label: sheet.name, range: r }];
  }
  // workbook
  const sheets = context.workbook.worksheets;
  sheets.load("items/name");
  await context.sync();

  const candidates: Array<{ name: string; range: Excel.Range }> = [];
  for (const sheet of sheets.items) {
    candidates.push({ name: sheet.name, range: sheet.getUsedRangeOrNullObject(true) });
  }
  await context.sync();
  return candidates
    .filter((c) => !c.range.isNullObject)
    .map((c) => ({ label: c.name, range: c.range }));
}

function matches(
  m: MatchCriteria,
  cellProps: Excel.CellProperties,
  cellValue: unknown,
  regex?: RegExp
): boolean {
  if (m.fillColor !== undefined) {
    if (readColor(cellProps?.format?.fill?.color) !== m.fillColor.toUpperCase()) return false;
  }
  if (m.fontColor !== undefined) {
    if (readColor(cellProps?.format?.font?.color) !== m.fontColor.toUpperCase()) return false;
  }
  if (m.bold !== undefined) {
    if ((cellProps?.format?.font?.bold ?? false) !== m.bold) return false;
  }
  if (m.italic !== undefined) {
    if ((cellProps?.format?.font?.italic ?? false) !== m.italic) return false;
  }
  if (regex) {
    const text = cellValue === null || cellValue === undefined ? "" : String(cellValue);
    if (!regex.test(text)) return false;
  }
  return true;
}

function transform(
  t: Transform,
  cellProps: Excel.CellProperties,
  cellValue: unknown,
  cellValueType: Excel.RangeValueType
): CellTransformResult {
  const result: CellTransformResult = {};
  const fontUpdate: { color?: string; bold?: boolean; italic?: boolean } = {};
  const formatUpdate: {
    fill?: { color: string };
    font?: typeof fontUpdate;
  } = {};

  if (t.fillColor !== undefined) {
    if (readColor(cellProps?.format?.fill?.color) !== t.fillColor.toUpperCase()) {
      formatUpdate.fill = { color: t.fillColor };
    }
  }
  if (
    t.fontColor !== undefined &&
    readColor(cellProps?.format?.font?.color) !== t.fontColor.toUpperCase()
  ) {
    fontUpdate.color = t.fontColor;
  }
  if (t.bold !== undefined && (cellProps?.format?.font?.bold ?? false) !== t.bold) {
    fontUpdate.bold = t.bold;
  }
  if (t.italic !== undefined && (cellProps?.format?.font?.italic ?? false) !== t.italic) {
    fontUpdate.italic = t.italic;
  }
  if (Object.keys(fontUpdate).length > 0) {
    formatUpdate.font = fontUpdate;
  }
  if (Object.keys(formatUpdate).length > 0) {
    result.props = { format: formatUpdate } as Excel.SettableCellProperties;
  }

  if (t.textCase !== undefined && cellValueType === Excel.RangeValueType.string) {
    const original = String(cellValue);
    const transformed = applyCase(original, t.textCase);
    if (transformed !== original) {
      result.value = transformed;
    }
  }

  return result;
}

function applyCase(s: string, c: TextCase): string {
  switch (c) {
    case "upper":
      return s.toUpperCase();
    case "lower":
      return s.toLowerCase();
    case "title":
      return s.replace(/\b\w/g, (ch) => ch.toUpperCase());
    case "trim":
      return s.trim();
  }
}

function readColor(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw.toString().toUpperCase();
}

function mergeProps(
  a: Excel.SettableCellProperties,
  b: Excel.SettableCellProperties
): Excel.SettableCellProperties {
  if (!b.format) return a;
  const aFormat = a.format ?? {};
  const bFormat = b.format;
  return {
    ...a,
    format: {
      ...aFormat,
      ...bFormat,
      fill: bFormat.fill ? { ...(aFormat.fill ?? {}), ...bFormat.fill } : aFormat.fill,
      font: bFormat.font ? { ...(aFormat.font ?? {}), ...bFormat.font } : aFormat.font,
    },
  } as Excel.SettableCellProperties;
}
